import React from 'react';
import { faRoute } from '@fortawesome/free-solid-svg-icons';
import BlogPost from '../BlogPost';
// Create: src/assets/images/blogs/libraries-to-use-cases.jpg
import librariesToUseCasesImg from '../../../assets/images/blogs/Libraries-to-UseCases.png';

const LibrariesToUseCases = () => (
    <BlogPost
        title="Libraries to Use Cases: Four Stages of Knowledge to Value"
        subtitle="The question isn't 'how to curate knowledge' - the librarian mindset. It's 'what's the path to drive the actual business?' - the value mindset."
        category="Strategy"
        categoryIcon={faRoute}
        color="#003366"
        date="March 2026"
        readTime="6 min read"
        image={librariesToUseCasesImg}
        imageAlt="Four stages of knowledge to value - from capture to scenario analysis"
        ctaHeading="Ready to turn knowledge into value?"
        ctaText="Book a discovery call and we'll show you how to move from knowledge capture to scenario analysis in weeks."
        seoDescription="The four-stage journey from knowledge capture to business value: encoding knowledge, emerging use cases, Data Object Graphs, and scenario analysis."
        seoKeywords="knowledge management, Data Object Graphs, scenario analysis, AI value, Nebulyx Cognition, business intelligence"
        slug="libraries-to-use-cases"
    >
        <p>
            Every organisation has more knowledge than it can use - API documentation,
            operating procedures, maintenance schedules, regulatory frameworks, and a
            huge amount locked in the heads of people who've been running things for
            decades.
        </p>

        <p>
            But the question isn't: "how to curate knowledge" - the librarian mindset.
            It's: <strong>"what's the path to drive the actual business?"</strong> -
            the value mindset.
        </p>

        <p>
            Here's how we think of that journey.
        </p>

        <h3>Stage 1: Knowledge Capture</h3>

        <p>
            A rail operator has system documentation describing every asset, route &amp;
            depot, maintenance procedures, and engineers who know the schedule says one
            thing but wet Tuesdays in November actually work differently. All of that is
            knowledge - structured &amp; unstructured, formal &amp; tacit.
        </p>

        <p>
            We encode it into our semantic AI (Nebulyx Cognition): system specs, procedures,
            interviews - extracting concepts, relationships &amp; structure. A working model
            that experts check, refine &amp; build upon. We're not capturing questions, just
            what the organisation knows.
        </p>

        <h3>Stage 2: Use Cases Emerge</h3>

        <p>
            Once captured, we query the knowledge with the real questions they've been
            trying to answer for years.
        </p>

        <blockquote className="blog-post-quote">
            "Which track sections maintained by contractor X are on routes affected by
            this timetable change?" - that spans asset, procurement &amp; operations domains.
        </blockquote>

        <h3>Stage 3: Model as Data Object Graphs</h3>

        <p>
            Next we extract and model use cases as executable processes (Data Object Graphs
            in Nebulyx AI Studio), bringing in live data - signalling feeds, track sensor
            readings, maintenance logs.
        </p>

        <p>
            The knowledge model told you what an asset is &amp; how it relates to a route.
            The Data Object Graph shows how it actually works in your business. The
            operational data tells you three sections of track are showing accelerated
            wear on the same corridor &amp; nobody connected it to a drainage change six
            months ago.
        </p>

        <p>
            <strong>Knowledge becomes insight</strong> - the model stops being a reference
            library &amp; starts being a lens on what's actually happening.
        </p>

        <h3>Stage 4: Scenario Analysis &amp; Planning</h3>

        <p>
            • What happens if we close that section for six months?<br />
            • Which services reroute &amp; what's the knock-on?<br />
            • If we accelerate renewal on corridor B, what maintenance can we defer on
            corridor C?
        </p>

        <p>
            Not describing what you know - <strong>simulating what could happen</strong>.
            The Data Object Graph gives you this: testing decisions before you make them.
        </p>

        <p>
            That's the shift from knowledge as an abstract library to knowledge as an
            operational capability.
        </p>

        <h3>This Isn't Sequential. It's Iterative.</h3>

        <p>
            This is Nebulyx AI:
        </p>

        <p>
            • Knowledge drives the use case.<br />
            • Data Object Graphs create the process.<br />
            • Operational data turns insight into action.<br />
            • Scenario analysis turns action into strategy.
        </p>

        <p>
            <strong>Knowledge is only valuable when it drives the business.</strong> Getting
            out of the library mindset to creating real business impact. 🎯
        </p>
    </BlogPost>
);

export default LibrariesToUseCases;
